/* LEGEND — floating SUPPLY card. Ported from legend/dilution.jsx (the version-schema build).
   Reads the server supply model (realtime GET /api/symbols/:sym/supply-timeline, built from the
   supply_timeline collections — src/apps/realtime/src/supply/model.ts):
     verdict + risk → share structure → warrant overhang vs float → the supply level that
     matters at this price → strike walls → structure flags → mini share trail.
   The trail opens the wide Float & Dilution timeline (SupplyTimeline) over the chart.
   Draggable / collapsible. */
import React from 'react';
import { Icon } from './Widgets';
import { DAY_MS, STALE_DAYS, tsOf, daysSince, fS, fD, fX, riskColor, verdictClass, stepSegments } from '../lib/supply';

const { useRef: useRefD } = React;
const h = React.createElement;

const DIcon = {
  drop: () => h('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinejoin: 'round' }, h('path', { d: 'M12 3c3.4 4.6 6 7.8 6 10.8a6 6 0 0 1-12 0C6 10.8 8.6 7.6 12 3Z' })),
  edgar: () => h('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }, h('path', { d: 'M14 4h6v6M20 4l-9 9M18 13v6H5V6h6' })),
  tl: () => h('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }, h('path', { d: 'M3 12h18M7 8v8M12 5v14M17 9v6' })),
};

// Tiny step sparkline of listed shares (today's basis) with split ticks and unverified stretches.
function Spark({ m }: { m: any }) {
  const pts = m.series.shares || [];
  const vals = pts.filter((p: any) => p.v != null);
  if (!vals.length) return null;
  const W = 272, H = 38;
  const t0 = tsOf(pts[0].date), t1 = Math.max(tsOf(m.dataThrough), t0 + DAY_MS);
  const vmax = Math.max(...vals.map((p: any) => p.v)) * 1.1 || 1;
  const X = (d: string) => 2 + (tsOf(d) - t0) / (t1 - t0) * (W - 4);
  const Y = (v: number) => H - 3 - v / vmax * (H - 8);
  const segs = stepSegments(pts, X, Y, W - 2, 2);
  const last = vals[vals.length - 1];
  return h('svg', { className: 'sup-spark', viewBox: '0 0 ' + W + ' ' + H, preserveAspectRatio: 'none' },
    (m.mask || []).filter((s: any) => tsOf(s.to) >= t0).map((s: any, i: number) => h('rect', {
      key: 'mk' + i, className: 'sp-mask', x: Math.max(2, X(s.from)), y: 0, width: Math.max(1.5, X(s.to) - Math.max(2, X(s.from))), height: H })),
    (m.splits || []).filter((sp: any) => tsOf(sp.date) >= t0).map((sp: any, i: number) => h('line', { key: i, x1: X(sp.date), x2: X(sp.date), y1: 0, y2: H, className: 'sp-split' })),
    segs.map((s, i) => h('path', { key: 'a' + i, d: s.d + ' V' + H + ' H' + s.x0 + 'Z', className: 'sp-area' })),
    segs.map((s, i) => h('path', { key: 'l' + i, d: s.d, className: 'sp-line' })),
    h('circle', { cx: W - 2, cy: Y(last.v), r: 2.6, className: 'sp-dot' }));
}

export function SupplyCard(props: any) {
  const { model: m, symbol, lastPrice, pos, collapsed, onMove, onToggle, onClose, onOpenTimeline, docked } = props;
  const cardRef = useRefD<any>(null);

  function startDrag(e: any) {
    if (e.target.closest('button') || e.target.closest('a')) return;
    e.preventDefault();
    const ox = (pos.x == null && cardRef.current) ? cardRef.current.offsetLeft : (pos.x || 0);
    const sx = e.clientX, sy = e.clientY, oy = pos.y;
    const move = (ev: any) => onMove({ x: ox + (ev.clientX - sx), y: oy + (ev.clientY - sy) });
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  }
  if (!m) return null;

  const price = lastPrice || 1, prec = price < 1 ? 4 : 2;
  const rc = riskColor(m.risk);
  const flOrd = m.float.ord || m.shares.ord || 1;          // ratios stay in ordinary shares
  const ohX = m.warrants.xFloat;
  const lv = m.warrants.levels || [];
  const maxSh = Math.max(1, ...lv.map((l: any) => l.shares));
  const live = lv.filter((l: any) => l.strike <= price), overhead = lv.filter((l: any) => l.strike > price);
  const liveSh = live.reduce((a: number, b: any) => a + b.shares, 0);
  const big = (arr: any[]) => arr.reduce((a, b) => (b.shares > a.shares ? b : a), arr[0]);
  const primary = overhead.length ? big(overhead) : null;
  const splitAgo = m.lastSplit ? daysSince(m.lastSplit.date) : null;
  const staleDays = daysSince(m.dataThrough);
  const ads = m.units === 'ADS';

  const head = h('div', { className: 'dil-head', onPointerDown: docked ? undefined : startDrag },
    h('span', { className: 'dil-ic' }, DIcon.drop()),
    h('span', { className: 'pc-sym' }, symbol),
    h('span', { className: 'dil-kicker' }, 'Supply'),
    h('span', { className: 'dil-verdict ' + verdictClass(m.verdict) }, m.verdict),
    h('div', { className: 'pc-actions' },
      h('button', { className: 'icon-btn sm', onClick: onToggle, title: collapsed ? 'Expand' : 'Collapse' }, Icon.chev()),
      h('button', { className: 'icon-btn sm', onClick: onClose, title: 'Close card' }, Icon.x())));

  const hero = h('div', { className: 'sup-hero' },
    h('div', { className: 'dh-score', style: { color: rc } }, h('span', { className: 'dh-num' }, m.risk), h('span', { className: 'dh-den' }, '/100')),
    h('div', { className: 'sup-hero-meta' },
      h('div', { className: 'sup-name', title: m.name }, m.name),
      h('div', { className: 'sup-sub' }, [m.regime, m.filingsKnown != null ? m.filingsKnown + ' filings' : null].filter(Boolean).join(' · '),
        m.readComplete === false ? h('span', { className: 'sup-warnc' }, 'partial') : h('span', { className: 'sup-ok' }, 'read ✓')),
      h('div', { className: 'sup-sub' },
        h('span', {
          className: staleDays != null && staleDays > STALE_DAYS ? 'sup-warnc' : '',
          title: 'Supply history runs to ' + m.dataThrough + ' — nothing newer is loaded',
        }, 'data to ' + fD(m.dataThrough) + (staleDays != null && staleDays > STALE_DAYS ? ' · ' + staleDays + 'd old' : '')))),
    h('div', { className: 'dh-gauge sup-gauge' },
      h('div', { className: 'dh-zones' }),
      h('div', { className: 'dh-gauge-fill', style: { width: m.risk + '%', background: rc } }),
      h('div', { className: 'dh-gauge-tick', style: { left: m.risk + '%' } })));

  // Float sub-line: the table date + accuracy, and the screener's float when it disagrees by >15%.
  const scr = m.float.screener, fv = m.float.value;
  const scrDiff = scr && fv && Math.abs(scr - fv) / fv > 0.15;
  const cell = (k: string, v: string, sub: any, cls?: string, title?: string) => h('div', { className: 'sup-cell', key: k, title },
    h('span', { className: 'sc-k' }, k), h('span', { className: 'sc-v num ' + (cls || '') }, v), h('span', { className: 'sc-sub' }, sub));
  const acc = (a: any) => (a ? String(a).toLowerCase() : '');
  const structure = h('div', { className: 'sup-grid' },
    cell('Float' + (ads ? ' · ADS' : ''), fS(fv),
      scrDiff ? h('span', { className: 'sup-warnc' }, 'screener ' + fS(scr)) : [fD(m.float.asOf), acc(m.float.accuracy)].filter(Boolean).join(' · '),
      '', 'Ownership table of ' + fD(m.float.asOf) + (m.float.accuracy ? ' (' + acc(m.float.accuracy) + ')' : '')
        + (scrDiff ? ' — the screener\'s float (symbols.details.floatShares) is ' + (scr as number).toLocaleString() : '')),
    cell('Outstanding' + (ads ? ' · ADS' : ''), fS(m.shares.value), [fD(m.shares.asOf), m.shares.corrected ? 'corrected' : acc(m.shares.accuracy)].filter(Boolean).join(' · ')),
    cell('Insiders', m.insiders.pct != null ? m.insiders.pct.toFixed(1) + '%' : '—', fS(m.insiders.shares) + ' sh'),
    cell('Fully diluted', fS(m.fullyDiluted.value), m.fullyDiluted.pctVsShares != null ? '+' + Math.round(m.fullyDiluted.pctVsShares).toLocaleString() + '% vs O/S' : '—',
      m.fullyDiluted.pctVsShares != null && m.fullyDiluted.pctVsShares > 100 ? 'down' : ''));

  const reg = m.warrants.registered || 0, caged = m.warrants.caged || 0, tot = Math.max(1, reg + caged);
  const overhang = h('div', { className: 'sup-oh' },
    h('div', { className: 'soh-top' },
      h('span', { className: 'soh-k' }, 'Warrant overhang'),
      h('span', { className: 'soh-x num' + (ohX != null && ohX >= 1 ? ' hot' : ohX != null && ohX >= 0.5 ? ' warm' : '') }, ohX != null ? fX(ohX) : '—', h('i', null, ' float'))),
    h('div', { className: 'soh-bar' },
      h('i', { className: 'reg', style: { width: (reg / tot * 100) + '%' } }),
      h('i', { className: 'cag', style: { width: (caged / tot * 100) + '%' } })),
    h('div', { className: 'soh-leg' },
      h('span', null, h('b', { className: 'num' }, fS(m.warrants.shares)), ' across ' + m.warrants.series + ' series'),
      h('span', null, h('i', { className: 'lg reg' }), fS(reg) + ' registered'),
      caged ? h('span', null, h('i', { className: 'lg cag' }), fS(caged) + ' caged') : null));

  const pctFloat = (sh: number) => Math.round(sh / flOrd * 100).toLocaleString() + '% of float';
  const headline = live.length
    ? h('div', { className: 'scaleout itm' },
        h('div', { className: 'so-top' }, h('span', { className: 'so-label' }, h('span', { className: 'so-dot' }), 'Supply live below price'), h('span', { className: 'so-dist num' }, fS(liveSh))),
        h('div', { className: 'so-price num' }, '$' + big(live).strike.toFixed(2)),
        h('div', { className: 'so-sub' }, h('b', null, fS(big(live).shares)), ' in the money · ', h('b', null, pctFloat(big(live).shares)), ' · every bid is their exit'))
    : primary ? h('div', { className: 'scaleout' },
        h('div', { className: 'so-top' }, h('span', { className: 'so-label' }, h('span', { className: 'so-dot' }), 'Must scale out'), h('span', { className: 'so-dist num' }, '+' + Math.round((primary.strike - price) / price * 100) + '%')),
        h('div', { className: 'so-price num' }, '$' + primary.strike.toFixed(2)),
        h('div', { className: 'so-sub' }, h('b', null, fS(primary.shares)), ' warrant wall · ', h('b', null, pctFloat(primary.shares)))) : null;

  const strikes = h('div', { className: 'dil-sec' },
    h('div', { className: 'dil-sec-title' }, 'Strikes vs $' + price.toFixed(prec), h('span', { className: 'dst-count' }, lv.length + ' levels')),
    lv.length ? h('div', { className: 'sup-strikes' }, lv.map((l: any, i: number) => {
      const itm = l.strike <= price, dist = Math.round((l.strike - price) / price * 100);
      return h('div', { className: 'sk-row' + (itm ? ' itm' : ''), key: i, title: (l.series || '') + (l.expiry ? ' · exp ' + l.expiry : '') + (l.registered ? ' · registered for resale' : l.registered === false ? ' · unregistered' : '') },
        h('span', { className: 'sk-px num' }, '$' + (l.strike >= 100 ? l.strike.toFixed(0) : l.strike.toFixed(2))),
        h('div', { className: 'sk-bar' }, h('i', { style: { width: Math.max(3, Math.sqrt(l.shares / maxSh) * 100) + '%' } })),
        h('span', { className: 'sk-sh num' }, fS(l.shares)),
        h('span', { className: 'sk-st num' }, itm ? 'ITM' : '+' + (dist > 999 ? Math.round(dist / 100) / 10 + 'k' : dist) + '%'));
    })) : h('div', { className: 'dil-none' }, 'No warrant strikes on file'));

  const flag = (k: string, on: boolean, txt?: string) => h('span', { className: 'sup-flag' + (on ? ' on' : ''), key: k }, h('i', null), k, txt ? h('b', null, txt) : null);
  const flags = h('div', { className: 'sup-flags' },
    flag('ATM', m.atm, m.atm ? 'live' : 'off'),
    flag('Equity line', m.equityLine, m.equityLine ? 'live' : 'off'),
    flag('Converts', m.converts > 0 || m.floatingConvert, m.floatingConvert ? 'floating' : String(m.converts || 0)),
    flag('Shelf', !!m.shelf, m.shelf ? m.shelf.form + (m.shelf.expiry ? ' → ' + String(m.shelf.expiry).slice(0, 7) : '') : 'none'),
    m.lastSplit ? flag('Split', splitAgo != null && splitAgo < 90, m.lastSplit.ratio + (splitAgo != null ? ' · ' + splitAgo + 'd' : '')) : null,
    m.splits12m ? flag('Splits 12m', m.splits12m >= 2, String(m.splits12m)) : null);

  const trail = h('button', { className: 'sup-trail', onClick: onOpenTimeline, title: 'Open the Float & Dilution timeline' },
    h('div', { className: 'st-top' }, h('span', null, 'Float & dilution timeline'), h('span', { className: 'st-go' }, DIcon.tl(), 'Open')),
    h(Spark, { m }),
    h('div', { className: 'st-foot' }, m.events.length + ' events · ' + m.splits.length + ' splits' + (m.mask.length ? ' · ' + m.mask.length + ' unverified' : '')));

  const foot = h('div', { className: 'dil-foot' },
    h('span', { className: 'dil-asof' }, [m.newest.form, fD(m.newest.date)].filter(Boolean).join(' ')),
    m.reviewCount ? h('span', { className: 'dil-stale', title: 'manual review flags in the read' }, m.reviewCount + ' review') : null,
    m.newest.url ? h('a', { className: 'dil-edgar', href: m.newest.url, target: '_blank', rel: 'noreferrer' }, 'EDGAR', DIcon.edgar()) : null);

  const cardStyle = docked ? null : (pos.x == null ? { right: '14px', top: pos.y + 'px' } : { left: pos.x + 'px', top: pos.y + 'px' });
  return h('div', { className: 'dil-card sup-card' + (collapsed ? ' collapsed' : '') + (docked ? ' docked' : ''), style: cardStyle, ref: cardRef },
    head,
    h('div', { className: 'dil-body' }, hero, structure, overhang, headline, strikes, flags, trail, foot));
}
